<template>
  <!-- Only render the container when there are notifications to show AND not in React Native -->
  <div
    v-if="
      visibleNotifications.length > 0 && !($reactNative && $reactNative.isApp)
    "
    class="top-notification-container flex flex-col gap-2"
  >
    <div
      v-for="notification in visibleNotifications"
      :key="notification.id"
      class="p-4 pr-12 rounded-lg relative text-center"
      :class="[
        getBackgroundColor(notification.type),
        getTextColor(notification.type),
      ]"
      role="alert"
    >
      <div class="inline-flex items-center justify-center gap-3">
        <svg
          class="flex-shrink-0 w-4 h-4"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path :d="getIcon(notification.type)" />
        </svg>
        <span class="sr-only">{{ notification.type }}</span>
        <div
          class="text-lg leading-6 font-medium"
          v-html="renderedMessage(notification.message)"
        />
      </div>
      <button
        v-if="notification.closeable"
        type="button"
        class="notification-close absolute right-4 top-1/2 -translate-y-1/2 rounded-lg p-1.5 inline-flex items-center justify-center h-8 w-8"
        @click="closeNotification(notification.id)"
        aria-label="Close"
      >
        <span class="sr-only">Close</span>
        <svg
          class="w-3 h-3"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 14 14"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMainStore } from "@/stores/main";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import type { NotificationType } from "@/types";
import MarkdownIt from "markdown-it";

const md = new MarkdownIt({
  html: true, // Enable HTML tags
  linkify: true, // Convert URL-like text to links
  breaks: true, // Convert '\n' to <br>
});

// Access the built-in React Native detection
const { $reactNative } = useNuxtApp();

// Format message with markdown and clean phone numbers
const renderedMessage = (message: string) => {
  if (!message) return "";

  let cleanContent = message
    .split("\n")
    .map((line: string) => line.trim())
    .join("\n");

  // Find and format phone number links
  // Example: Call [02 5126 0000](tel:02 5126 0000)
  cleanContent = cleanContent.replace(
    /\[(\d[\d\s]+)\]\(tel:[\d\s]+\)/g,
    (match: string, phone: string) => {
      const cleanPhone = phone.replace(/\s+/g, "");
      return `[${phone}](tel:${cleanPhone})`;
    }
  );

  return md.render(cleanContent);
};

const mainStore = useMainStore();
const { notifications } = storeToRefs(mainStore);
console.log(notifications);
const visibleNotifications = computed(() => {
  // If in React Native WebView, don't show any notifications
  // Use the built-in reactNative.isApp property
  if ($reactNative && $reactNative.isApp) {
    console.log("TopNotification: Hiding notifications in React Native app");
    return [];
  }

  // Otherwise, show active notifications that haven't expired
  return notifications.value.filter((notification) => {
    const endDate = new Date(notification.endDate);
    const now = new Date();
    return notification.active && endDate > now;
  });
});

const getBackgroundColor = (type: NotificationType["type"]) => {
  switch (type) {
    case "warning":
      return "bg-warning";
    case "error":
      return "bg-red-100";
    case "success":
      return "bg-green-100";
    default:
      return "bg-blue-100";
  }
};

const getTextColor = (type: NotificationType["type"]) => {
  switch (type) {
    case "warning":
      return "text-black";
    case "error":
      return "text-red-900";
    case "success":
      return "text-green-900";
    default:
      return "text-blue-900";
  }
};

const getIcon = (type: NotificationType["type"]) => {
  switch (type) {
    case "warning":
      return "M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z";
    case "error":
      return "M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z";
    case "success":
      return "M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z";
    default:
      return "M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z";
  }
};

const closeNotification = (notificationId: string) => {
  const index = notifications.value.findIndex((n) => n.id === notificationId);
  if (index !== -1) {
    notifications.value[index].active = false;
  }
};
</script>

<style scoped>
.top-notification-container {
  margin-top: 35px;
  background: #f4e7ff !important;
}

.top-notification-container .flex.items-center.p-4.rounded-lg {
  background: #f4e7ff !important;
}
@media (min-width: 1024px) {
  .top-notification-container {
    margin-top: 35px;
    margin-bottom: 8px;
  }
}
.notification-close {
  background: #f4e7ff !important;
  color: #9900f1 !important;
  border: none !important;
  box-shadow: none !important;
}
.notification-close svg {
  stroke: #9900f1 !important;
  color: #9900f1 !important;
  fill: none !important;
}
</style>
